import React, { useEffect, useState, useRef } from "react";
import {
    Card, CardBody, Row, Col, Input, Button, Table,
    Modal, ModalHeader, ModalBody,
    Badge
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { formatIST } from "../../Helpers/Utils";

const SalesList = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(s => s.customer.customer);
    const loading = useSelector(s => s.loading.loading);

    /* ================= LIST ================= */
    const [rows, setRows] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [search, setSearch] = useState("");

    const [today, setToday] = useState(false);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [sortOrder, setSortOrder] = useState("desc");

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const limit = 10;

    const searchTimer = useRef(null);

    /* ================= LOAD SALES ================= */
    const loadSales = async () => {
        dispatch(loadingTrue());

        const params = {
            outlet_id: user.outlet_id,
            q: search,
            limit,
            offset: (page - 1) * limit,
            sort_order: sortOrder
        };

        if (today) params.today = 1;
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;

        const qs = new URLSearchParams(params).toString();

        const res = await Handler({
            method: "get",
            url: `/sales/search.php?${qs}`
        });

        dispatch(loadingFalse());

        if (!res?.success) return toast.error(res?.msg);

        setRows(res.data.records || []);
        setTotal(res.data.pagination.total || 0);
    };

    /* 🔍 DEBOUNCED SEARCH */
    useEffect(() => {
        if (!user?.outlet_id) return;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setPage(1);
            setSearch(searchText);
        }, 500);

        return () => clearTimeout(searchTimer.current);
    }, [searchText, user?.outlet_id]);

    /* 🔥 FILTER CHANGE */
    useEffect(() => {
        if (!user?.outlet_id) return;
        setPage(1);
    }, [search, today, startDate, endDate, sortOrder, user?.outlet_id]);

    useEffect(() => {
        if (!user?.outlet_id) return;
        loadSales();
    }, [page, search, today, startDate, endDate, sortOrder, user?.outlet_id]);


    /* ================= UI ================= */
    return (
        <>
            <Row className="mb-3">
                <Col><h5 className="fw-bold">Total Sale</h5></Col>
            </Row>

            <Card className="shadow-sm">
                <CardBody>

                    {/* ================= FILTERS ================= */}
                    <Row className="mb-3 g-2 align-items-end">
                        <Col md={3}>
                            <Input
                                placeholder="Invoice / Sale ID"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                            />
                        </Col>

                        <Col md={2}>
                            <Input type="date" value={startDate}
                                onChange={e => {
                                    setToday(false);
                                    setStartDate(e.target.value);
                                }} />
                        </Col>

                        <Col md={2}>
                            <Input type="date" value={endDate}
                                onChange={e => {
                                    setToday(false);
                                    setEndDate(e.target.value);
                                }} />
                        </Col>

                        <Col md={2}>
                            <Input type="select" value={sortOrder}
                                onChange={e => setSortOrder(e.target.value)}>
                                <option value="desc">Newest First</option>
                                <option value="asc">Oldest First</option>
                            </Input>
                        </Col>

                        <Col md={1}>
                            <Button
                                color={today ? "success" : "secondary"}
                                onClick={() => {
                                    setStartDate("");
                                    setEndDate("");
                                    setToday(t => !t);
                                }}>
                                Today
                            </Button>
                        </Col>

                        <Col md={2} className="text-end">
                            <Button color="dark" onClick={() => navigate("/pos")}>
                                + New Sale
                            </Button>
                        </Col>
                    </Row>

                    {/* ================= TABLE ================= */}
                    <Table hover responsive className="align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>#</th>
                                <th>ID</th>
                                <th>Customer</th>
                                <th>Date</th>
                                <th>Status</th>
                                <th className="text-end">Sub Total</th>
                                <th className="text-end">GST</th>
                                <th className="text-end">Grand Total</th>
                                <th className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>

                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="text-center text-muted">
                                        No records
                                    </td>
                                </tr>
                            )}

                            {rows?.map((r, i) => (
                                <tr key={r.sale_id} className={r.payment_status === "RETURN" ? "return" : ""}>
                                    <td>{(page - 1) * limit + i + 1}</td>
                                    <td className="fw-bold">{r.sale_id}</td>
                                    <td className="fw-bold">
                                        {r.customer?.id
                                            ? `${r.customer.id} - ${r.customer.name}`
                                            : "Walk-in Customer"}
                                    </td>
                                    <td>
                                        {/* {new Date(r.created_at).toLocaleString()} */}
                                        {formatIST(r?.created_at)}
                                    </td>
                                    <td><Badge color={r?.payment_status === "RETURN" ? "warning" : r?.payment_status === "PAID" ? "success" : r?.payment_status === "UNPAID" ? "danger" : ""}>{r?.payment_status}</Badge></td>
                                    <td className="text-end fw-bold"> ₹ {Number(r.taxable_amount).toFixed(2)}</td>
                                    <td className="text-end fw-bold"> ₹ {Number(r.cgst) + Number(r.sgst)}</td>
                                    <td className="text-end fw-bold"> ₹ {Number(r.total_amount).toFixed(2)}</td>
                                    <td className="text-center d-flex gap-3 justify-content-end">
                                        {(r.payment_status === "UNPAID") && (
                                            <div className="d-flex justify-content-end gap-3">
                                                <Button disabled={loading} size="sm" color="dark"
                                                    onClick={() => navigate(`/pos-pay/${r.sale_id}`)}>
                                                    <i className="bx bx-edit"></i> Add (or) Pay
                                                </Button>
                                            </div>
                                        )}
                                        {(r.payment_status === "PAID") && (
                                            <div className="d-flex justify-content-end gap-3">
                                                <Button disabled={loading} size="sm" color="warning"
                                                    onClick={() => navigate(`/pos-edit/${r.sale_id}/${r.payment_id}`)}>
                                                    <i className="bx bx-edit"></i>
                                                </Button>
                                            </div>
                                        )}
                                        {(r.payment_status === "PAID") && (
                                            <div className="d-flex justify-content-end gap-3">
                                                <Button disabled={loading} size="sm" color="danger"
                                                    onClick={() => navigate("/sales-return", { state: { sale_id: r.sale_id } })}>
                                                    Return
                                                </Button>
                                            </div>
                                        )}

                                    </td>
                                </tr>
                            ))}

                        </tbody>
                    </Table>

                    <Pagination
                        total={total}
                        page={page}
                        limit={limit}
                        onPrev={() => setPage(p => Math.max(1, p - 1))}
                        onNext={() => setPage(p => p + 1)}
                    />

                </CardBody>
            </Card>
        </>
    );
};

export default SalesList;
